import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { errText, fmt } from "@/lib/api";
import { ReportBody } from "@/console/ReportPage";
import { Loading } from "@/console/ui";
import { TRI_LOGO, ORG_NAME } from "@/lib/brand";

export default function SharedReport() {
  const { token } = useParams();
  const [stage, setStage] = useState("email"); // email | code | report | error
  const [meta, setMeta] = useState(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow, noarchive";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  useEffect(() => {
    api
      .get(`/public/report/${token}/meta`)
      .then(({ data: d }) => setMeta(d))
      .catch((e) => {
        setError(errText(e));
        setStage("error");
      });
  }, [token]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (stage === "report" && data)
    return (
      <div className="console-root min-h-screen p-4 sm:p-8">
        <div className="max-w-5xl mx-auto">
          <p className="font-data text-[11px] text-[#7A6D66] mb-4" data-testid="shared-report-expiry">
            Access expires {fmt(data.expires_at)} · viewing as {data.watermark}
          </p>
          <ReportBody
            data={data}
            snapshotUrl={(snapId) =>
              `${process.env.REACT_APP_BACKEND_URL}/api/public/report/snapshot/${snapId}`
            }
          />
        </div>
      </div>
    );

  if (stage !== "error" && !meta)
    return (
      <div className="console-root min-h-screen flex items-center justify-center p-4">
        <Loading label="Opening secure report link…" testId="shared-report-loading" />
      </div>
    );

  return (
    <div className="console-root min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-sm border border-[#E7DED7] bg-white rounded-lg p-6 shadow-sm" data-testid="shared-report-gate">
        <img src={TRI_LOGO} alt={ORG_NAME} style={{ height: 34, width: "auto" }} className="mb-4" />
        <p className="font-data text-[11px] uppercase tracking-widest text-[#7A6D66]">
          Pragati · confidential report
        </p>
        {stage === "error" ? (
          <>
            <h1 className="font-console text-xl font-medium mt-3">Link unavailable</h1>
            <p className="text-sm text-[#6B5F59] mt-2" data-testid="shared-report-error">
              {error}
            </p>
          </>
        ) : (
          <>
            <h1 className="font-console text-xl font-medium mt-3">Verify your email</h1>
            <p className="text-xs text-[#6B5F59] mt-2">
              This report was shared with {meta?.email_hint || "one address"}. We will email a
              one-time code to confirm it is you. The code expires in 10 minutes.
            </p>
            {stage === "email" && (
              <>
                <input
                  type="email"
                  placeholder="your email"
                  value={email}
                  data-testid="share-otp-email-input"
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-5 w-full bg-[#FBF8F5] border border-[#DCD1C9] rounded px-3 py-2.5 text-sm"
                />
                <button
                  className="mt-3 w-full bg-[#7B2617] hover:bg-[#5E1C10] text-white disabled:opacity-40 rounded py-2.5 text-sm"
                  disabled={busy || !email.includes("@")}
                  data-testid="request-otp-button"
                  onClick={() =>
                    run(async () => {
                      await api.post(`/public/report/${token}/request-otp`, { email });
                      setStage("code");
                    })
                  }
                >
                  {busy ? "Sending…" : "Email me a code"}
                </button>
              </>
            )}
            {stage === "code" && (
              <>
                <input
                  inputMode="numeric"
                  placeholder="6-digit code"
                  value={code}
                  data-testid="share-otp-code-input"
                  onChange={(e) => setCode(e.target.value)}
                  className="mt-5 w-full bg-[#FBF8F5] border border-[#DCD1C9] rounded px-3 py-2.5 text-lg font-data tracking-[0.4em] text-center"
                />
                <button
                  className="mt-3 w-full bg-[#7B2617] hover:bg-[#5E1C10] text-white disabled:opacity-40 rounded py-2.5 text-sm"
                  disabled={busy || code.trim().length < 4}
                  data-testid="verify-otp-button"
                  onClick={() =>
                    run(async () => {
                      await api.post(`/public/report/${token}/verify-otp`, { email, code });
                      const { data: d } = await api.get("/public/report/view");
                      setData(d);
                      setStage("report");
                    })
                  }
                >
                  {busy ? "Checking…" : "Open report"}
                </button>
                <button
                  className="mt-2 w-full text-xs text-[#7A6D66] hover:text-[#7B2617]"
                  data-testid="resend-otp-button"
                  onClick={() => run(() => api.post(`/public/report/${token}/request-otp`, { email }))}
                >
                  Send a new code
                </button>
              </>
            )}
            {error && (
              <p className="text-sm text-rose-700 mt-3" data-testid="shared-report-error">
                {error}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
